import React, { useState, useRef, useEffect } from 'react';
import { X, Check } from 'lucide-react';
import Portal from './Portal';

const ImageCropModal = ({ isOpen, onClose, imageFile, onCropComplete, aspectRatio = 1 }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [scale, setScale] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 200, height: 200 });
  const [isDraggingCrop, setIsDraggingCrop] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeHandle, setResizeHandle] = useState(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageNaturalSize, setImageNaturalSize] = useState({ width: 0, height: 0 });
  const [modalSize, setModalSize] = useState({ width: 800, height: 600 });

  // Bloquear scroll quando o modal estiver aberto (especialmente no mobile)
  useEffect(() => {
    if (isOpen) {
      // Salvar o valor atual dos estilos
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const originalTop = document.body.style.top;
      const originalWidth = document.body.style.width;
      const originalHeight = document.body.style.height;
      
      // Calcular a posição atual do scroll
      const scrollY = window.scrollY;
      
      // Bloquear scroll no desktop
      document.body.style.overflow = 'hidden';
      
      // Bloquear scroll no mobile - usar position fixed
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      
      // Prevenir scroll apenas no overlay (fora do modal)
      const preventScroll = (e) => {
        // Permitir todos os eventos dentro do modal
        const modalContent = e.target.closest('[data-modal-content]');
        if (modalContent) {
          return; // Permitir interação dentro do modal
        }
        
        // Verificar se é um elemento dentro do overlay do modal
        const modalOverlay = e.target.closest('[data-modal-overlay]');
        if (modalOverlay) {
          // Se está dentro do overlay mas não no conteúdo, pode ser interação com a área de crop
          // Verificar se é a área de crop ou handles
          const isCropArea = e.target.closest('[data-crop-area]') || 
                            e.target.closest('[data-resize-handle]');
          if (isCropArea) {
            return; // Permitir interação com crop
          }
        }
        
        // Bloquear apenas scroll do body/overlay quando não é interação com o modal
        if (e.target === document.body || e.target === document.documentElement || 
            (modalOverlay && !modalContent)) {
          e.preventDefault();
        }
      };
      
      // Adicionar listener apenas no body para prevenir scroll
      document.body.addEventListener('touchmove', preventScroll, { passive: false });
      
      // Restaurar quando o modal fechar
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
        document.body.style.top = originalTop;
        document.body.style.width = originalWidth;
        document.body.style.height = originalHeight;
        
        // Remover listener
        document.body.removeEventListener('touchmove', preventScroll);
        
        // Restaurar posição do scroll
        if (scrollY) {
          window.scrollTo(0, scrollY);
        }
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (imageFile && isOpen) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImageSrc(e.target.result);
        setScale(1);
        setImagePosition({ x: 0, y: 0 });
        setImageLoaded(false);
        setImageNaturalSize({ width: 0, height: 0 });
      };
      reader.readAsDataURL(imageFile);
    }
  }, [imageFile, isOpen]);

  useEffect(() => {
    if (!imageSrc) return;
    
    // Aguardar um pouco para garantir que o DOM está pronto
    const timer = setTimeout(() => {
      const img = imageRef.current;
      const container = containerRef.current;
      
      if (!img || !container) {
        return;
      }
      
      const handleImageLoad = () => {
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
          setImageNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
          
          // Calcular tamanho ideal do modal baseado na imagem
          // Header: ~60px, Controles: ~80px
          const headerHeight = 60;
          const controlsHeight = 80;
          const totalExtraHeight = headerHeight + controlsHeight;
          const totalExtraWidth = 0; // Sem padding horizontal - modal se ajusta à imagem
          
          // Padding mínimo no overlay para garantir que o modal não toque as bordas
          const overlayPadding = 20;
          
          // Espaço máximo disponível na tela (considerando padding do overlay)
          const maxModalWidth = window.innerWidth - (overlayPadding * 2);
          const maxModalHeight = window.innerHeight - (overlayPadding * 2);
          
          // Espaço disponível para a imagem (sem header e controles)
          const availableWidth = maxModalWidth - totalExtraWidth;
          const availableHeight = maxModalHeight - totalExtraHeight;
          
          // Calcular escala para caber a imagem perfeitamente
          const scaleX = availableWidth / img.naturalWidth;
          const scaleY = availableHeight / img.naturalHeight;
          const imageScale = Math.min(scaleX, scaleY, 1);
          
          // Tamanho da imagem exibida (escala perfeita)
          const imgDisplayWidth = img.naturalWidth * imageScale;
          const imgDisplayHeight = img.naturalHeight * imageScale;
          
          // Tamanho do modal = tamanho da imagem + espaços fixos (sem padding extra)
          const modalWidth = imgDisplayWidth + totalExtraWidth;
          const modalHeight = imgDisplayHeight + totalExtraHeight;
          
          setModalSize({ 
            width: Math.min(modalWidth, maxModalWidth), 
            height: Math.min(modalHeight, maxModalHeight) 
          });
          
          // Aguardar container estar pronto
          const checkContainer = () => {
            const rect = container.getBoundingClientRect();
            const currentContainerWidth = rect.width;
            const currentContainerHeight = rect.height;
            
            if (currentContainerWidth <= 0 || currentContainerHeight <= 0) {
              setTimeout(checkContainer, 100);
              return;
            }
            
            // Container deve ter exatamente o tamanho da imagem exibida
            const containerWidth = imgDisplayWidth;
            const containerHeight = imgDisplayHeight;
            
            // Escala inicial = escala da imagem (1:1 quando possível)
            const initialScale = imageScale;
            
            setScale(initialScale);
            
            // Calcular tamanho da área de crop respeitando aspectRatio (ex: 1 = quadrado, 3/5 = vertical)
            const ratio = aspectRatio || 1;
            let cropWidth = imgDisplayWidth;
            let cropHeight = cropWidth / ratio;
            
            // Se a altura calculada ultrapassar a altura da imagem, usar altura máxima
            if (cropHeight > imgDisplayHeight) {
              cropHeight = imgDisplayHeight;
              cropWidth = cropHeight * ratio;
            }
            if (cropWidth > imgDisplayWidth) {
              cropWidth = imgDisplayWidth;
              cropHeight = cropWidth / ratio;
            }
            
            // Garantir tamanho mínimo
            const minSize = 200;
            if (cropWidth < minSize) {
              cropWidth = Math.min(minSize, imgDisplayWidth);
              cropHeight = cropWidth / ratio;
            }
            if (cropHeight < minSize) {
              cropHeight = Math.min(minSize, imgDisplayHeight);
              cropWidth = cropHeight * ratio;
            }
            if (cropWidth > imgDisplayWidth || cropHeight > imgDisplayHeight) {
              cropWidth = Math.min(cropWidth, imgDisplayWidth);
              cropHeight = Math.min(cropHeight, imgDisplayHeight);
              cropHeight = Math.min(cropHeight, cropWidth / ratio);
              cropWidth = cropHeight * ratio;
            }
            
            // Centralizar área de crop
            const cropX = (containerWidth - cropWidth) / 2;
            const cropY = (containerHeight - cropHeight) / 2;
            
            setCropArea({
              x: Math.max(0, cropX),
              y: Math.max(0, cropY),
              width: cropWidth,
              height: cropHeight
            });
            
            // Imagem sempre fica travada em 0,0 (sem movimento)
            setImagePosition({
              x: 0,
              y: 0
            });
            
            setImageLoaded(true);
          };
          
          // Aguardar um frame antes de verificar container
          requestAnimationFrame(() => {
            setTimeout(checkContainer, 100);
          });
        }
      };
      
      // Verificar se a imagem já está carregada
      if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
        handleImageLoad();
      } else {
        img.onload = handleImageLoad;
        // Fallback: se onload não disparar em 2 segundos, tentar mesmo assim
        setTimeout(() => {
          if (!imageLoaded && img.naturalWidth > 0 && img.naturalHeight > 0) {
            handleImageLoad();
          }
        }, 2000);
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [imageSrc, imageLoaded]);

  const getImageDisplaySize = () => {
    if (!imageNaturalSize.width || !imageNaturalSize.height) {
      return { width: 400, height: 400 };
    }
    return {
      width: imageNaturalSize.width * scale,
      height: imageNaturalSize.height * scale
    };
  };

  const getResizeHandles = () => {
    return [
      { position: 'nw', x: cropArea.x, y: cropArea.y },
      { position: 'ne', x: cropArea.x + cropArea.width, y: cropArea.y },
      { position: 'sw', x: cropArea.x, y: cropArea.y + cropArea.height },
      { position: 'se', x: cropArea.x + cropArea.width, y: cropArea.y + cropArea.height }
    ];
  };

  useEffect(() => {
    const getEventCoordinates = (e) => {
      // Suportar tanto mouse quanto touch
      if (e.touches && e.touches.length > 0) {
        return { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
      return { x: e.clientX, y: e.clientY };
    };

    const handleGlobalMove = (e) => {
      if (!imageLoaded || !containerRef.current) return;
      
      const coords = getEventCoordinates(e);
      const rect = containerRef.current.getBoundingClientRect();
      // Coordenadas relativas à imagem (sem padding)
      const x = coords.x - rect.left;
      const y = coords.y - rect.top;
      
      if (isResizing && resizeHandle) {
        const deltaX = x - dragStart.x;
        const deltaY = y - dragStart.y;
        
        let newCropArea = {
          x: dragStart.cropX,
          y: dragStart.cropY,
          width: dragStart.cropW,
          height: dragStart.cropH
        };
        
        if (resizeHandle === 'nw') {
          newCropArea.x = dragStart.cropX + deltaX;
          newCropArea.y = dragStart.cropY + deltaY;
          newCropArea.width = dragStart.cropW - deltaX;
          newCropArea.height = dragStart.cropH - deltaY;
        } else if (resizeHandle === 'ne') {
          newCropArea.y = dragStart.cropY + deltaY;
          newCropArea.width = dragStart.cropW + deltaX;
          newCropArea.height = dragStart.cropH - deltaY;
        } else if (resizeHandle === 'sw') {
          newCropArea.x = dragStart.cropX + deltaX;
          newCropArea.width = dragStart.cropW - deltaX;
          newCropArea.height = dragStart.cropH + deltaY;
        } else if (resizeHandle === 'se') {
          newCropArea.width = dragStart.cropW + deltaX;
          newCropArea.height = dragStart.cropH + deltaY;
        }
        
        // Manter aspect ratio
        if (aspectRatio) {
          const currentRatio = newCropArea.width / newCropArea.height;
          if (Math.abs(currentRatio - aspectRatio) > 0.01) {
            if (resizeHandle === 'se' || resizeHandle === 'nw') {
              newCropArea.height = newCropArea.width / aspectRatio;
              if (resizeHandle === 'nw') {
                newCropArea.y = dragStart.cropY + dragStart.cropH - newCropArea.height;
              }
            } else {
              newCropArea.width = newCropArea.height * aspectRatio;
              if (resizeHandle === 'nw' || resizeHandle === 'sw') {
                newCropArea.x = dragStart.cropX + dragStart.cropW - newCropArea.width;
              }
            }
          }
        }
        
        // Limitar tamanho (respeitar aspectRatio e bordas da imagem)
        const minSize = 50;
        const imgDisplaySize = getImageDisplaySize();
        const imageWidth = imgDisplaySize.width;
        const imageHeight = imgDisplaySize.height;
        
        if (newCropArea.width < minSize) {
          newCropArea.width = minSize;
          if (aspectRatio) newCropArea.height = minSize / aspectRatio;
        }
        if (newCropArea.height < minSize) {
          newCropArea.height = minSize;
          if (aspectRatio) newCropArea.width = minSize * aspectRatio;
        }
        if (newCropArea.width > imageWidth) {
          newCropArea.width = imageWidth;
          if (aspectRatio) newCropArea.height = imageWidth / aspectRatio;
        }
        if (newCropArea.height > imageHeight) {
          newCropArea.height = imageHeight;
          if (aspectRatio) newCropArea.width = imageHeight * aspectRatio;
        }
        
        // Limitar dentro da área da imagem
        
        if (newCropArea.x < 0) {
          const diff = 0 - newCropArea.x;
          newCropArea.x = 0;
          if (resizeHandle === 'nw' || resizeHandle === 'sw') {
            newCropArea.width -= diff;
          }
        }
        if (newCropArea.y < 0) {
          const diff = 0 - newCropArea.y;
          newCropArea.y = 0;
          if (resizeHandle === 'nw' || resizeHandle === 'ne') {
            newCropArea.height -= diff;
          }
        }
        if (newCropArea.x + newCropArea.width > imageWidth) {
          newCropArea.width = imageWidth - newCropArea.x;
          if (aspectRatio) newCropArea.height = newCropArea.width / aspectRatio;
        }
        if (newCropArea.y + newCropArea.height > imageHeight) {
          newCropArea.height = imageHeight - newCropArea.y;
          if (aspectRatio) newCropArea.width = newCropArea.height * aspectRatio;
        }
        
        setCropArea(newCropArea);
      } else if (isDraggingCrop) {
        const newX = x - dragStart.x;
        const newY = y - dragStart.y;
        
        // Usar o tamanho real da imagem exibida (sem padding)
        const imgDisplaySize = getImageDisplaySize();
        const imageWidth = imgDisplaySize.width;
        const imageHeight = imgDisplaySize.height;
        
        // O crop pode se mover de 0 até (tamanho da imagem - tamanho do crop)
        const maxX = imageWidth - cropArea.width;
        const maxY = imageHeight - cropArea.height;
        
        setCropArea({
          ...cropArea,
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        });
      }
    };

    const handleGlobalUp = () => {
      setIsDraggingCrop(false);
      setIsResizing(false);
      setResizeHandle(null);
    };

    if (isDraggingCrop || isResizing) {
      // Suporte para mouse
      document.addEventListener('mousemove', handleGlobalMove);
      document.addEventListener('mouseup', handleGlobalUp);
      
      // Suporte para touch (mobile)
      document.addEventListener('touchmove', handleGlobalMove, { passive: false });
      document.addEventListener('touchend', handleGlobalUp);
      document.addEventListener('touchcancel', handleGlobalUp);
      
      return () => {
        document.removeEventListener('mousemove', handleGlobalMove);
        document.removeEventListener('mouseup', handleGlobalUp);
        document.removeEventListener('touchmove', handleGlobalMove);
        document.removeEventListener('touchend', handleGlobalUp);
        document.removeEventListener('touchcancel', handleGlobalUp);
      };
    }
  }, [isDraggingCrop, isResizing, dragStart, resizeHandle, cropArea, imageLoaded, aspectRatio]);

  const getTouchCoordinates = (e) => {
    if (e.touches && e.touches.length > 0) {
      return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    return { x: e.clientX, y: e.clientY };
  };

  const handleCropAreaMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!imageLoaded) return;
    
    const coords = getTouchCoordinates(e);
    const rect = containerRef.current.getBoundingClientRect();
    // Coordenadas relativas à imagem (sem padding)
    const x = coords.x - rect.left;
    const y = coords.y - rect.top;
    
    setIsDraggingCrop(true);
    setDragStart({ x: x - cropArea.x, y: y - cropArea.y });
  };

  const handleResizeMouseDown = (e, handle) => {
    e.preventDefault();
    e.stopPropagation();
    if (!imageLoaded) return;
    
    const coords = getTouchCoordinates(e);
    const rect = containerRef.current.getBoundingClientRect();
    // Coordenadas relativas à imagem (sem padding)
    const x = coords.x - rect.left;
    const y = coords.y - rect.top;
    
    setIsResizing(true);
    setResizeHandle(handle.position);
    setDragStart({ x, y, cropX: cropArea.x, cropY: cropArea.y, cropW: cropArea.width, cropH: cropArea.height });
  };

  const handleCrop = () => {
    if (!imageSrc || !imageRef.current || !canvasRef.current) {
      console.error('Erro: elementos necessários não estão disponíveis');
      return;
    }
    
    if (!imageLoaded || !imageNaturalSize.width || !imageNaturalSize.height) {
      console.error('Erro: imagem não carregou completamente');
      // Tentar mesmo assim se tivermos imageSrc
      if (!imageSrc) return;
    }
    
    const img = imageRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Usar tamanhos naturais se disponíveis, senão usar os calculados
    const imgWidth = imageNaturalSize.width || img.naturalWidth || 400;
    const imgHeight = imageNaturalSize.height || img.naturalHeight || 400;
    const imgDisplaySize = getImageDisplaySize();
    
    const scaleX = imgWidth / imgDisplaySize.width;
    const scaleY = imgHeight / imgDisplaySize.height;
    
    // Calcular posição do crop relativa à imagem
    const cropXInImage = (cropArea.x - imagePosition.x) * scaleX;
    const cropYInImage = (cropArea.y - imagePosition.y) * scaleY;
    const cropWidthInImage = cropArea.width * scaleX;
    const cropHeightInImage = cropArea.height * scaleY;
    
    // Garantir que o crop está dentro da imagem
    const finalCropX = Math.max(0, Math.min(cropXInImage, imgWidth));
    const finalCropY = Math.max(0, Math.min(cropYInImage, imgHeight));
    const finalCropWidth = Math.min(cropWidthInImage, imgWidth - finalCropX);
    const finalCropHeight = Math.min(cropHeightInImage, imgHeight - finalCropY);
    
    if (finalCropWidth <= 0 || finalCropHeight <= 0) {
      console.error('Erro: área de crop inválida');
      return;
    }
    
    // Configurar canvas
    canvas.width = finalCropWidth;
    canvas.height = finalCropHeight;
    
    // Desenhar imagem cortada
    ctx.drawImage(
      img,
      finalCropX, finalCropY, finalCropWidth, finalCropHeight,
      0, 0, finalCropWidth, finalCropHeight
    );
    
    // Converter para blob
    canvas.toBlob((blob) => {
      if (blob) {
        const croppedFile = new File([blob], imageFile.name, { type: imageFile.type });
        onCropComplete(croppedFile);
        // Fechar o modal após o crop ser completado
        onClose();
      } else {
        console.error('Erro ao criar blob da imagem cortada');
      }
    }, imageFile.type, 0.95);
  };

  if (!isOpen || !imageSrc) return null;

  const imgDisplaySize = getImageDisplaySize();

  const handleOverlayClick = (e) => {
    // Fechar apenas se clicar diretamente no overlay, não no conteúdo do modal
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <Portal>
      <div 
        className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10000] p-5"
        data-modal-overlay
        onClick={handleOverlayClick}
      >
        <div 
          className="bg-card-light dark:bg-card-dark rounded-lg shadow-xl flex flex-col overflow-hidden"
          data-modal-content
          style={{
            width: `${modalSize.width}px`,
            height: `${modalSize.height}px`,
            maxWidth: 'calc(100vw - 40px)',
            maxHeight: 'calc(100vh - 40px)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border-light dark:border-border-dark">
            <h2 className="text-lg font-semibold text-foreground-light dark:text-foreground-dark">
              Ajustar Enquadramento
            </h2>
            <button
              onClick={onClose}
              className="text-muted-light dark:text-muted-dark hover:text-foreground-light dark:hover:text-foreground-dark transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Canvas Area */}
          <div 
            ref={containerRef}
            className="relative overflow-hidden flex-shrink-0"
            style={{ 
              cursor: isDraggingCrop ? 'grabbing' : isResizing ? 'nwse-resize' : 'default',
              width: imageLoaded && imageNaturalSize.width ? `${imageNaturalSize.width * scale}px` : '100%',
              height: imageLoaded && imageNaturalSize.height ? `${imageNaturalSize.height * scale}px` : '400px',
              padding: '0px',
              margin: '0px',
              backgroundColor: 'transparent',
              flex: '0 0 auto'
            }}
          >
            {imageSrc && (
              <>
                <img
                  ref={imageRef}
                  src={imageSrc}
                  alt="Preview"
                  className="select-none"
                  style={{
                    position: 'absolute',
                    left: '0px',
                    top: '0px',
                    width: imageLoaded && imageNaturalSize.width ? `${imageNaturalSize.width * scale}px` : '100%',
                    height: imageLoaded && imageNaturalSize.height ? `${imageNaturalSize.height * scale}px` : '100%',
                    userSelect: 'none',
                    pointerEvents: 'none',
                    zIndex: 1,
                    objectFit: 'contain',
                    display: 'block',
                    margin: 0,
                    padding: 0
                  }}
                />
                
                {/* Debug: Mostrar status */}
                {!imageLoaded && (
                  <div className="absolute top-2 left-2 bg-yellow-500 text-black px-2 py-1 rounded text-xs z-50">
                    Carregando imagem...
                  </div>
                )}
                
                {/* Área de crop - renderizar PRIMEIRO para estar acima */}
                {imageLoaded && (
                  <>
                    <div
                      data-crop-area
                      className="absolute border-2 border-white shadow-lg"
                      style={{
                        left: `${cropArea.x}px`,
                        top: `${cropArea.y}px`,
                        width: `${cropArea.width}px`,
                        height: `${cropArea.height}px`,
                        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
                        zIndex: 10,
                        cursor: isDraggingCrop ? 'grabbing' : 'move',
                        touchAction: 'none',
                        pointerEvents: 'auto',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      }}
                      onMouseDown={handleCropAreaMouseDown}
                      onTouchStart={handleCropAreaMouseDown}
                    />
                    {/* Handles de redimensionamento */}
                    {getResizeHandles().map((handle, index) => (
                      <div
                        key={index}
                        data-resize-handle
                        className="absolute bg-white border-2 border-primary-600 rounded-full hover:bg-primary-200"
                        style={{
                          left: `${handle.x - 6}px`,
                          top: `${handle.y - 6}px`,
                          width: '16px',
                          height: '16px',
                          cursor: `${handle.position}-resize`,
                          touchAction: 'none',
                          zIndex: 11,
                          pointerEvents: 'auto'
                        }}
                        onMouseDown={(e) => handleResizeMouseDown(e, handle)}
                        onTouchStart={(e) => handleResizeMouseDown(e, handle)}
                      />
                    ))}
                  </>
                )}
                
                {/* Overlay escuro - renderizar DEPOIS para ficar abaixo */}
                {imageLoaded && (
                  <div 
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: 'rgba(0, 0, 0, 0.5)',
                      clipPath: `polygon(
                        0% 0%, 
                        0% 100%, 
                        ${cropArea.x}px 100%, 
                        ${cropArea.x}px ${cropArea.y}px, 
                        ${cropArea.x + cropArea.width}px ${cropArea.y}px, 
                        ${cropArea.x + cropArea.width}px ${cropArea.y + cropArea.height}px, 
                        ${cropArea.x}px ${cropArea.y + cropArea.height}px, 
                        ${cropArea.x}px 100%, 
                        100% 100%, 
                        100% 0%
                      )`,
                      zIndex: 2
                    }}
                  />
                )}
              </>
            )}
          </div>

          {/* Controls */}
          <div className="p-4 border-t border-border-light dark:border-border-dark flex items-center justify-end">
            <div className="flex items-center space-x-2">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg bg-background-light dark:bg-background-dark hover:bg-gray-200 dark:hover:bg-gray-700 text-foreground-light dark:text-foreground-dark transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleCrop}
                className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white transition-colors flex items-center space-x-2"
              >
                <Check className="w-4 h-4" />
                <span>Confirmar</span>
              </button>
            </div>
          </div>
        </div>
        
        {/* Canvas oculto para processamento */}
        <canvas ref={canvasRef} className="hidden" />
      </div>
    </Portal>
  );
};

export default ImageCropModal;
